import { useCallback, useEffect } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import useDocumentViewerViewModel from './DocumentViewerViewModel'
import { useRenderPdf } from '@hook/useRenderPdf'
import cn from 'clsx'

import CircleSpinner from '@components/shared/CircleSpinner'
import { MdOutlineWarningAmber } from 'react-icons/md'

import './DocumentViewer.css'

const DocumentViewer = ({
    className,
    record,
    pageIndex,
    onHaveMatchesByPageIndex = () => {},
}) => {
    
    const pdfLoading = useWireValue(store.pdfRendererLoading)
    
    const viewModel = useDocumentViewerViewModel({
        record,
        pageIndex,
    })
    
    const onRenderPdfError = useCallback(err => {
        
        switch (err.name) {
            case 'MissingPDFException':
                viewModel.setError('Document not found')
                break
            default:
                console.error('onRenderPdfError', err)
                viewModel.setError('An unexpected error occurred when trying to load the document')
        }
        
    }, [viewModel.setError])
    
    const onBeforePdfRender = useCallback(() => viewModel.setPageRendered(false), [viewModel.setPageRendered])
    const onAfterPdfRender = useCallback(() => viewModel.setPageRendered(true), [viewModel.setPageRendered])
    
    useRenderPdf({
        enabled: true, //!!viewModel.refDocumentCanvas.current,
        url: viewModel.pdfUrl,
        pageIndex,
        setPageCount: viewModel.setPageCount,
        getCanvas: () => viewModel.refDocumentCanvas.current,
        onError: onRenderPdfError,
        onBeforeRender: onBeforePdfRender,
        onAfterRender: onAfterPdfRender,
    })
    
    useEffect(() => {
        onHaveMatchesByPageIndex(viewModel.resultsByPageIndex)
    }, [onHaveMatchesByPageIndex, viewModel.resultsByPageIndex])
    
    useEffect(() => { viewModel.renderOverlays() }, [
        pageIndex,
        pdfLoading,
        viewModel.pageRendered,
        viewModel.record,
        viewModel.refRenderCanvas.current,
        viewModel.refDocumentCanvas.current,
        viewModel.refOverlaysCanvas.current,
    ])
    // This gets computed after `record.pages`, so wait for it to be ready
    if (!pdfLoading && !viewModel.pngUrl?.length)
        return (
            <div className={cn('DocumentViewer relative', className)}>
                <div className="flex items-center justify-center">
                    <p>Image Not Available</p>
                </div>
            </div>
        )
    
    return (
        
        <div className={cn('DocumentViewer relative', className)}>
            
            {/* @todo 2023-12-27 can remove this after next release */}
            {/* <div>
                <pre>
                    <code>{JSON.stringify({
                        dbg: 2,
                        pageIndex,
                        page: viewModel.page,
                        pngUrl: viewModel.pngUrl,
                        pdfLoading,
                        resultsByPageIndex: viewModel.resultsByPageIndex,
                    }, null, 4)}</code>
                </pre>
            </div> */}
            
            {!viewModel.error && (
                <canvas
                    ref={viewModel.refRenderCanvas}
                    className="w-full min-h-[80%]" />
            )}
            
            {pdfLoading && (
                <CircleSpinner className="absolute-centered" />
            )}
            
            {viewModel.error && (
                <div role="alert" className="alert alert-warning">
                    {viewModel.error}
                </div>
            )}
            
            {/* Weird edge case that only happens if pre-processing failed */}
            {!pdfLoading && viewModel.record?.pages?.length <= 0 && (
                <div className={cn('DocumentViewer flex flex-col justify-center', className)}>
                    <div role="alert" className="alert alert-warning">
                        <div><MdOutlineWarningAmber className="text-4xl" /></div>
                        <p><i>No pages found for this document.</i></p>
                    </div>
                    {/* <div><pre><code>{JSON.stringify(record, null, 4)}</code></pre></div> */}
                </div>
            )}
        
        </div>
        
    )
    
}

export default DocumentViewer
